import { FormEvent, useEffect, useRef, useState } from 'react';
import type { AnnexForSaleResult, CustomerListItem, MeResponse, PayMethods, PriceTypeItem, ProductListItem, QuickButton, RegisterRef, SaleInput, SalePreviewResult, ShiftSummary } from '@kassa/shared';
import { api, ApiError } from '../api';
import { NoteField } from '../components/NotesPanel';
import { annexFileUrl } from '../api/contracts';
import { CARD_TERMINAL_INITIAL, CardTerminal, type CardTerminalState } from '../components/CardTerminal';
import { formatMoney, hasPermission, kop, lineAmount, UNIT_LABEL } from '../format';
import { DISCOUNT_LIMIT_PCT, discountPercent, needsPin } from '../returns';
import { percentOf, splitDiscount } from '../discount';
import { heldList, holdCheck, removeHeld, type HeldCheck } from '../held';
import { CameraScan, cameraScanSupported } from '../components/CameraScan';
import { SALE_PENDING_KEY } from '../salePending';
import { Receipt } from './Receipt';
import { DebtPayment } from './DebtPayment';
import { DateField } from '../components/DateField';

interface Props {
  me: MeResponse;
  onBack: () => void;
  /** Şirkət satışı (KT-55): VÖEN-li alıcı, qiymət növü, köçürmə ilə ödəniş. */
  company?: boolean;
}



interface CartLine {
  productId: string;
  name: string;
  unit: string;
  price: number;
  qty: number;
  discount: number;
  /** Seçilmiş qiymət növündə qiymət yoxdur — pərakəndə qiymət götürülüb (serverdən). */
  fallback?: boolean;
}

// Kassa satışı: növbə açılmayıbsa əvvəl açılış nağdı soruşulur; səbət, ödəniş, qalıq pul.
export function Sale({ me, onBack, company = false }: Props) {
  const [registerId, setRegisterId] = useState('');
  // Bu kassada açıq ödəniş növləri (KT-95): obyekt ∧ rol icazəsi; null — hələ bilinmir (server yenə yoxlayır)
  const [methods, setMethods] = useState<PayMethods | null>(null);
  useEffect(() => {
    if (!registerId) return;
    let cancelled = false;
    api
      .salePayMethods(registerId)
      .then((m) => !cancelled && setMethods(m))
      .catch(() => !cancelled && setMethods(null));
    return () => {
      cancelled = true;
    };
  }, [registerId]);
  const allow = (m: keyof PayMethods) => methods?.[m] !== false;
  // Bağlı növün dəyəri state-də qalıb nağdı azaltmasın; müştəri götürüləndə köçürmə və nisyə də sıfırlanır
  useEffect(() => {
    if (methods?.card === false) setCard('0');
    if (methods?.transfer === false) setTransfer('0');
    if (methods?.credit === false) setCredit('0');
    if (methods?.bonus === false) setBonus('0');
  }, [methods]);
  const [shift, setShift] = useState<ShiftSummary | null>(null);
  const [shiftChecked, setShiftChecked] = useState(false);
  const [openingCash, setOpeningCash] = useState('0');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [customerId, setCustomerId] = useState('');
  // customerId-reset: müştəri götürüləndə köçürmə və nisyə (müştərisiz olmur) sıfırlanır
  useEffect(() => {
    setBonus('0');
    setBonusBalance(null);
    if (!customerId) {
      setTransfer('0');
      setCredit('0');
      return;
    }
    // Bonus balansı (KT-96): modul açıqdırsa və ya balans qalıbsa xərclənə bilər
    let cancelled = false;
    api
      .customerBonus(customerId)
      .then((b) => !cancelled && setBonusBalance(b.balance))
      .catch(() => !cancelled && setBonusBalance(null));
    return () => {
      cancelled = true;
    };
  }, [customerId]);
  const [debtCustomerId, setDebtCustomerId] = useState('');
  const [card, setCard] = useState('0');
  const [credit, setCredit] = useState('0');
  const [transfer, setTransfer] = useState('0');
  const [checkDisc, setCheckDisc] = useState('');
  const [held, setHeld] = useState<HeldCheck[]>([]);
  // Serverə göndərilmiş (cavabı bilinməyən) çeklərin UUID-ləri
  const attempted = useRef(new Set<string>());
  const [holdError, setHoldError] = useState<string | null>(null);
  const [checkMode, setCheckMode] = useState<'pct' | 'amount'>('pct');
  const [bonus, setBonus] = useState('0');
  // Kart hissəsi terminaldan (KT-91): təsdiqlənmiş əməliyyat satışla göndərilir
  const [cardTerm, setCardTerm] = useState<CardTerminalState>(CARD_TERMINAL_INITIAL);
  const [bonusBalance, setBonusBalance] = useState<number | null>(null);
  // Borc ödənişi satış ekranında daimi blok deyil — düymə ilə açılır (KT-95)
  const [showDebt, setShowDebt] = useState(false);
  const [priceType, setPriceType] = useState('topdan');
  const [priceTypes, setPriceTypes] = useState<PriceTypeItem[]>([]);
  const [preview, setPreview] = useState<SalePreviewResult | null>(null);
  const [dueOn, setDueOn] = useState('');
  const [cashGiven, setCashGiven] = useState('');
  const [note, setNote] = useState('');
  const [pin, setPin] = useState('');
  const [needPin, setNeedPin] = useState(false);
  const [saleId, setSaleId] = useState<string>(() => crypto.randomUUID());
  const [receipt, setReceipt] = useState<{ id: string; displayNo: string; total: number; change: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canCredit = hasPermission(me, 'sale.credit');
  const canPay = hasPermission(me, 'customer.payment');

  useEffect(() => {
    api
      .shiftRegisters()
      .then((r: RegisterRef[]) => {
        if (r.length > 0) setRegisterId(r[0].id);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Kassalar yüklənmədi'));
  }, []);

  useEffect(() => {
    if (!registerId) return;
    setShiftChecked(false);
    api
      .shiftCurrent(registerId)
      .then((s) => setShift(s))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Növbə yüklənmədi'))
      .finally(() => setShiftChecked(true));
  }, [registerId]);

  useEffect(() => {
    api
      .salePriceTypes()
      .then((list) => {
        setPriceTypes(list);
        // Defolt «topdan» yoxdursa — ilk qeyri-defolt növ
        setPriceType((cur) => (list.some((t) => t.code === cur) ? cur : list.find((t) => !t.isDefault)?.code ?? cur));
      })
      .catch(() => setPriceTypes([]));
  }, []);

  const loadCustomers = () =>
    api
      .customers({ limit: 100 })
      .then((r) => setCustomers(r.items))
      .catch(() => setCustomers([]));

  useEffect(() => {
    if (!canCredit && !canPay) return;
    loadCustomers();
    // Siyahı yalnız icazələr dəyişəndə yüklənir; ödənişdən sonra qalıq loadCustomers ilə yenilənir
  }, [canCredit, canPay]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setFound([]);
      return;
    }
    // cancelled: axtarış dəyişəndən sonra gələn köhnə cavab siyahını qaytarmasın (Enter səhv/ikiqat mal əlavə etməsin)
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .products({ q: search.trim(), limit: 10 })
        .then((r) => !cancelled && setFound(r.items))
        .catch(() => !cancelled && setFound([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  const cardAmount = Number(card) || 0;
  const creditAmount = Number(credit) || 0;
  // Köçürmə kassa satışında da (KT-95) — müştəri seçilməlidir
  const transferAmount = Number(transfer) || 0;
  const bonusAmount = Number(bonus) || 0;
  // Cəm, qiymətlər və xəbərdarlıqlar serverdən (önizləmə) — səbət, qiymət növü və ödəniş dəyişəndə yenilənir.
  // Önizləmə gələnə qədər: tam qəpiklə (float çıxma yoxdur)
  const total = preview ? preview.total : cart.reduce((s, l) => s + kop(lineAmount(l.qty, l.price, l.discount)), 0) / 100;
  const cashDue = preview ? preview.cashDue : Math.max(0, kop(total) - kop(cardAmount) - kop(creditAmount) - kop(transferAmount) - kop(bonusAmount)) / 100;
  // Ödəniş cəmi (kart + nisyə + köçürmə + bonus) çekin cəmindən çox ola bilməz (məs. çek endirimi cəmi bonusdan aşağı saldı) —
  // server 400 qaytarır; kassir səbəbi əvvəlcədən görsün (tam qəpiklə)
  const overpaid = kop(cardAmount) + kop(creditAmount) + kop(transferAmount) + kop(bonusAmount) > kop(total);
  // Terminal rejimində kart hissəsi təsdiqsiz (və ya başqa məbləğlə) göndərilmir
  // siyahı hələ gəlməyib və ya yüklənmədisə də (pul terminalda çıxmadan «kart» yazılmasın)
  const cardPending = cardAmount > 0 && (!cardTerm.ready || (cardTerm.active && (cardTerm.approved === null || kop(Number(cardTerm.approved.amount)) !== kop(cardAmount))));
  const given = cashGiven === '' ? null : Number(cashGiven);
  const change = given === null ? 0 : Math.max(0, kop(given) - kop(cashDue)) / 100;

  const previewKey = JSON.stringify({
    r: registerId,
    c: customerId,
    k: company,
    t: company ? priceType : null,
    p: cart.map((l) => [l.productId, l.qty, l.discount]),
    m: [cardAmount, creditAmount, transferAmount, bonusAmount],
  });
  useEffect(() => {
    if (!registerId || cart.length === 0) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .salePreview({
          registerId,
          customerId: customerId || null,
          lines: cart.map((l) => ({ productId: l.productId, qty: l.qty, discount: l.discount })),
          card: cardAmount,
          credit: creditAmount,
          transfer: transferAmount,
          bonus: bonusAmount,
          saleKind: company ? 'company' : 'market',
          priceType: company ? priceType : null,
        })
        .then((p) => {
          setPreview(p);
          setCart((cur) =>
            cur.map((l) => {
              const x = p.lines.find((y) => y.productId === l.productId);
              return x ? { ...l, price: x.price, fallback: x.fallback } : l;
            }),
          );
        })
        .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Önizləmə alınmadı'));
    }, 250);
    return () => clearTimeout(t);
    // Asılılıq previewKey-dədir: qiymətlər cart-ı dəyişir, sonsuz dövrə olmasın deyə cart siyahıda deyil
  }, [previewKey]);

  // qty: qutu barkodu skan ediləndə qutudakı vahid sayı (KT-101), əks halda 1
  function addProduct(p: Pick<ProductListItem, 'id' | 'name' | 'unitCode' | 'retailPrice'>, qty = 1) {
    if (p.retailPrice === null) {
      setError(`«${p.name}» üçün qiymət təyin olunmayıb`);
      return;
    }
    setCart((cur) => {
      const i = cur.findIndex((l) => l.productId === p.id);
      if (i >= 0) return cur.map((l, k) => (k === i ? { ...l, qty: l.qty + qty } : l));
      return [...cur, { productId: p.id, name: p.name, unit: p.unitCode, price: p.retailPrice!, qty, discount: 0 }];
    });
    setSearch('');
    setFound([]);
  }

  useEffect(() => setHeld(heldList(registerId)), [registerId]);
  // Skanerdən «Satışa əlavə et» (KT-108): mal səbətə bir dəfə düşür
  useEffect(() => {
    let pending: { productId?: unknown } | null = null;
    try {
      const raw = window.sessionStorage.getItem(SALE_PENDING_KEY);
      window.sessionStorage.removeItem(SALE_PENDING_KEY);
      pending = raw ? (JSON.parse(raw) as { productId?: unknown }) : null;
    } catch (e) {
      console.error('kassa.sale: skanerdən gələn mal oxunmadı', e instanceof Error ? e.message : e);
      pending = null;
    }
    if (typeof pending?.productId !== 'string') return;
    api
      .productCard(pending.productId)
      .then((c) => addProduct({ id: c.id, name: c.name, unitCode: c.unitCode, retailPrice: c.retailPrice }))
      .catch((e: unknown) => {
        console.error('kassa.sale: skanerdən gələn mal', e instanceof Error ? e.message : e);
        setError(e instanceof ApiError && e.status === 404 ? 'Skanerdən gələn mal tapılmadı' : `Skanerdən gələn mal əlavə olunmadı: ${e instanceof Error ? e.message : 'xəta'}`);
      });
  }, []);
  // Tez düymələr (KT-101) — kassanın obyekti üzrə
  const [quick, setQuick] = useState<QuickButton[]>([]);
  const [scanning, setScanning] = useState(false);
  useEffect(() => {
    if (!registerId) return;
    api.saleQuickButtons(registerId).then(setQuick).catch(() => setQuick([]));
  }, [registerId]);

  // Dəqiq kodla (barkod, PLU, qısa kod): ilk tapılan səbətə; qutu barkodu — qutudakı vahid sayı (KT-101)
  function addByCode(q: string) {
    if (q.length < 2) return;
    api
      .products({ q, limit: 1 })
      .then((r) => {
        if (r.items[0]) addProduct(r.items[0], r.items[0].matchedQty ?? 1);
        else setError(`«${q}» tapılmadı`);
      })
      .catch(() => setError('Axtarış alınmadı'));
  }

  // Gözləməyə alma (KT-101): çek öz UUID-i ilə cihazda saxlanır; geri çağırılanda həmin UUID ilə satılır
  function holdCurrent(): boolean {
    if (cart.length === 0) return true;
    // Göndərilib cavabı gəlməyən çek (serverdə yazılmış ola bilər) gözləməyə alınmır: sonra dəyişdirilsə, eyni UUID köhnə sənədi qaytarardı
    if (attempted.current.has(saleId)) {
      setHoldError('Bu çek artıq göndərilib, cavab gəlməyib — əvvəl yenidən «Sat» basın');
      return false;
    }
    const ok = holdCheck(registerId, { id: saleId, heldAt: new Date().toISOString(), customerId, lines: cart });
    if (!ok) {
      setHoldError('Çek gözləməyə alınmadı: brauzer yaddaşı əlçatmazdır');
      return false;
    }
    setHoldError(null);
    return true;
  }

  function hold() {
    if (!holdCurrent()) return;
    resetSale();
    setHeld(heldList(registerId));
  }

  function recall(h: HeldCheck) {
    if (!holdCurrent()) return;
    removeHeld(registerId, h.id);
    setCart(h.lines);
    setCustomerId(h.customerId);
    setSaleId(h.id);
    setHeld(heldList(registerId));
  }

  // Çek endirimi (KT-101): sətirlərə məbləğ payına görə bölünür (tam qəpik); limit və PİN sətir endirimi kimi yoxlanır
  function applyCheckDiscount() {
    const bases = cart.map((l) => kop(lineAmount(l.qty, l.price, l.discount)));
    const sum = bases.reduce((a, b) => a + b, 0);
    const want = checkMode === 'pct' ? percentOf(sum, Number(checkDisc)) : kop(Number(checkDisc));
    const shares = splitDiscount(bases, want);
    setCart((cur) => cur.map((l, i) => ({ ...l, discount: (kop(l.discount) + shares[i]) / 100 })));
    setCheckDisc('');
  }

  function setDiscount(i: number, discount: number) {
    setCart((cur) => cur.map((l, k) => (k === i ? { ...l, discount: Math.max(0, discount) } : l)));
  }

  function setQty(i: number, qty: number) {
    setCart((cur) => (qty <= 0 ? cur.filter((_, k) => k !== i) : cur.map((l, k) => (k === i ? { ...l, qty } : l))));
  }

  function resetSale() {
    setCart([]);
    setCustomerId('');
    setCard('0');
    setCredit('0');
    setTransfer('0');
    setBonus('0');
    setCardTerm((s) => ({ ...s, approved: null }));
    setDueOn('');
    setCashGiven('');
    setNote('');
    setPin('');
    setNeedPin(false);
    setSaleId(crypto.randomUUID());
  }

  const open = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      setShift(await api.shiftOpen({ registerId, openingCash: Number(openingCash) }));
    });
  };

  const sell = (e: FormEvent) => {
    e.preventDefault();
    // Növbə yalnız nağd və ya kart hissəsi olanda lazımdır (şirkət satışı, KT-57)
    if (!shift && !(company && cashDue === 0 && cardAmount === 0)) return;
    const body: SaleInput = {
      id: saleId,
      registerId,
      customerId: customerId || null,
      lines: cart.map((l) => ({ productId: l.productId, qty: l.qty, discount: l.discount })),
      card: cardAmount,
      cardTxnIds: cardTerm.approved ? [cardTerm.approved.id] : undefined,
      cardManualTerminalId: cardTerm.manualTerminalId ?? undefined,
      credit: creditAmount,
      transfer: transferAmount,
      bonus: bonusAmount,
      saleKind: company ? 'company' : 'market',
      priceType: company ? priceType : null,
      dueOn: creditAmount > 0 && dueOn ? dueOn : null,
      cashGiven: given,
      pin: pin !== '' ? pin : null,
      note: note.trim() || null,
    };
    // PİN yalnız bu sorğu üçündür: sorğudan sonra ekrandan təmizlənir
    setPin('');
    run(async () => {
      try {
        const r = await api.sale(body);
        setReceipt({ id: r.id, displayNo: r.displayNo, total: r.total, change: r.change });
        resetSale();
      } catch (err) {
        // Şəbəkə xətası (ApiError deyil): sənəd yazılmış ola bilər
        if (!(err instanceof ApiError)) attempted.current.add(body.id);
        // PİN tələb olunursa pəncərə açılır; sənəd yaranmayıb, eyni UUID ilə yenidən göndərilir
        if (err instanceof ApiError && needsPin(err.code)) setNeedPin(true);
        throw err;
      }
    });
  };

  if (!shiftChecked) return <div className="page-center muted">Yüklənir…</div>;

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn back-home" onClick={onBack}>
          ← Ana səhifə
        </button>
        <span className="brand">{company ? 'Şirkət satışı' : 'Satış'}</span>
        {shift && <span className="muted small">{shift.registerCode} · növbə açıqdır</span>}
      </header>
      <main className="page">
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {receipt && (
          <section className="card" aria-live="polite">
            <h2>{receipt.displayNo}</h2>
            <p>
              Cəm {formatMoney(receipt.total)} · qalıq pul <strong>{formatMoney(receipt.change)}</strong>
            </p>
            <button type="button" className="btn-small" onClick={() => setReceipt(null)}>
              Yeni satış
            </button>
            {company && hasPermission(me, 'contract.manage') && <SaleAnnex docId={receipt.id} />}
            <Receipt id={receipt.id} autoPrint />
          </section>
        )}

        {!shift && !company && (
          <section className="card">
            <h2>Növbə açıq deyil</h2>
            <p className="muted">Satış üçün əvvəl növbə açılır. Açılış nağdını daxil edin.</p>
            {hasPermission(me, 'shift.manage') && (
              <form onSubmit={open} className="form-grid">
                <label>
                  Açılış nağdı
                  <input type="number" min="0" step="0.01" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} />
                </label>
                <button type="submit" className="btn-primary" disabled={busy}>
                  Növbəni aç
                </button>
              </form>
            )}
          </section>
        )}

        {!shift && company && (
          <p className="muted small">Növbə açıq deyil: yalnız tam köçürmə və ya nisyə ilə yazılır. Nağd və kart üçün növbəni Kassa növbəsi ekranında açın.</p>
        )}

        {(shift || company) && (
          <>
            <section className="card">
              <label className="field">
                Məhsul axtarışı
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  // Enter (klaviatura və barkod skaneri): ilk tapılan mal səbətə (KT-86)
                  // Skaner Enter-i axtarışın gecikməsindən (250 ms) tez göndərir: siyahıya yox, cari mətnlə dəqiq sorğuya baxılır
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    addByCode(search.trim());
                  }}
                  placeholder="Ad, barkod və ya kod"
                />
              </label>
              {cameraScanSupported() && (
                <button type="button" className="btn-small" onClick={() => setScanning(true)}>
                  Kamera ilə barkod
                </button>
              )}
              {scanning && (
                <CameraScan
                  onClose={() => setScanning(false)}
                  onCode={(code) => {
                    setScanning(false);
                    addByCode(code);
                  }}
                />
              )}
              {quick.length > 0 && (
                <div className="quick-grid" role="group" aria-label="Tez düymələr">
                  {quick.map((b) => (
                    <button
                      key={b.productId}
                      type="button"
                      className="quick-btn"
                      disabled={b.retailPrice === null}
                      onClick={() => addProduct({ id: b.productId, name: b.name, unitCode: b.unit, retailPrice: b.retailPrice })}
                    >
                      <span className="quick-initial" aria-hidden="true">
                        {b.name.trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="quick-name">{b.name}</span>
                      <span className="mono small">{formatMoney(b.retailPrice)}</span>
                    </button>
                  ))}
                </div>
              )}
              {found.length > 0 && (
                <ul className="product-list">
                  {found.map((p) => (
                    <li key={p.id}>
                      <button type="button" className="product-row" onClick={() => addProduct(p)}>
                        <span className="product-name">{p.name}</span>
                        <span className="muted small">{p.retailPrice === null ? 'qiymətsiz' : formatMoney(p.retailPrice)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card">
              <h2>Səbət</h2>
              {cart.length === 0 && <p className="muted">Səbət boşdur.</p>}
              {cart.map((l, i) => (
                <div className="purchase-line" key={l.productId}>
                  <span className="product-name">{l.name}</span>
                  <label>
                    Miqdar
                    <input type="number" min="0" step="0.001" value={l.qty} onChange={(e) => setQty(i, Number(e.target.value))} />
                  </label>
                  <label>
                    Endirim
                    <input type="number" min="0" step="0.01" value={l.discount} onChange={(e) => setDiscount(i, Number(e.target.value))} />
                  </label>
                  {discountPercent(l.price * l.qty, l.discount) > DISCOUNT_LIMIT_PCT && (
                    <span className="muted small">limit {DISCOUNT_LIMIT_PCT}% — menecer PİN-i ola bilər</span>
                  )}
                  <span className="cart-price muted small">
                    Qiymət {formatMoney(l.price)} / {UNIT_LABEL[l.unit] ?? l.unit}
                    {l.fallback && ' · pərakəndə qiymət'}
                  </span>
                  <span className="cart-sum">
                    Cəmi <strong>{formatMoney(lineAmount(l.qty, l.price, l.discount))}</strong>
                  </span>
                  <button type="button" className="link-btn" onClick={() => setQty(i, 0)}>
                    Sil
                  </button>
                </div>
              ))}
              {(cart.length > 0 || held.length > 0) && (
                <div className="held-bar">
                  {cart.length > 0 && (
                    <button type="button" className="btn-small" onClick={hold}>
                      Gözləməyə al
                    </button>
                  )}
                  {held.length > 0 && (
                    <details className="held-list">
                      <summary className="btn-small">Gözləyən çeklər ({held.length})</summary>
                      <ul>
                        {held.map((h) => (
                          <li key={h.id}>
                            <span>
                              {new Date(h.heldAt).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baku' })} · {h.lines.length} mal ·{' '}
                              {formatMoney(h.lines.reduce((a, l) => a + kop(lineAmount(l.qty, l.price, l.discount)), 0) / 100)}
                            </span>
                            <button type="button" className="btn-small" onClick={() => recall(h)}>
                              Geri çağır
                            </button>
                            <button type="button" className="link-btn" onClick={() => (removeHeld(registerId, h.id), setHeld(heldList(registerId)))}>
                              Sil
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {holdError && (
                    <p className="bad small" role="alert">
                      {holdError}
                    </p>
                  )}
                </div>
              )}
              {cart.length > 0 && hasPermission(me, 'sale.discount') && (
                <div className="check-discount" role="group" aria-label="Çek endirimi">
                  <label>
                    Çek endirimi
                    <input type="number" min="0" step="0.01" value={checkDisc} onChange={(e) => setCheckDisc(e.target.value)} />
                  </label>
                  <select aria-label="Endirim növü" value={checkMode} onChange={(e) => setCheckMode(e.target.value as 'pct' | 'amount')}>
                    <option value="pct">%</option>
                    <option value="amount">₼</option>
                  </select>
                  <button type="button" className="btn-small" onClick={applyCheckDiscount} disabled={!(Number(checkDisc) > 0) || (checkMode === 'pct' && Number(checkDisc) > 100)}>
                    Tətbiq et
                  </button>
                  {cart.some((l) => l.discount > 0) && (
                    <button type="button" className="link-btn" onClick={() => setCart((cur) => cur.map((l) => ({ ...l, discount: 0 })))}>
                      Endirimləri sıfırla
                    </button>
                  )}
                </div>
              )}
            </section>

            <section className="card">
              <form onSubmit={sell} className="form-grid">
                {(canCredit || canPay || company) && (
                  <label>
                    {company ? 'Alıcı (VÖEN-li şirkət)' : 'Müştəri (nisyə və köçürmə üçün)'}
                    <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required={company}>
                      <option value="">{company ? 'Seçin' : 'Yoxdur'}</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {allow('card') && (
                  <label>
                    Kart
                    <input type="number" min="0" step="0.01" value={card} onChange={(e) => setCard(e.target.value)} disabled={cardTerm.approved !== null} />
                  </label>
                )}
                {allow('card') && registerId && cardAmount > 0 && <CardTerminal registerId={registerId} amount={cardAmount} state={cardTerm} onChange={setCardTerm} />}
                {company && (
                  <label>
                    Qiymət növü
                    <select value={priceType} onChange={(e) => setPriceType(e.target.value)}>
                      {priceTypes.map((t) => (
                        <option key={t.code} value={t.code}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {/* Bonus (KT-96): müştəri seçilib, balans var; balansdan və cəmdən çox olmaz (server də yoxlayır) */}
                {customerId && allow('bonus') && bonusBalance !== null && (me.modules?.includes('bonus') || bonusBalance > 0) && (
                  <label>
                    Bonus (balans {formatMoney(bonusBalance)})
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={Math.min(bonusBalance, total)}
                      value={bonus}
                      onChange={(e) => setBonus(e.target.value)}
                      disabled={bonusBalance <= 0}
                    />
                  </label>
                )}
                {overpaid && (
                  <p className="bad" role="alert">
                    Kart, nisyə, köçürmə və bonusun cəmi çekin cəmindən ({formatMoney(total)}) çoxdur — azaldın.
                  </p>
                )}
                {allow('transfer') && (
                  <label>
                    Köçürmə (gözlənilir)
                    <input type="number" min="0" step="0.01" value={transfer} onChange={(e) => setTransfer(e.target.value)} disabled={!customerId} />
                  </label>
                )}
                {canCredit && allow('credit') && (
                  <label>
                    Nisyə
                    <input type="number" min="0" step="0.01" value={credit} onChange={(e) => setCredit(e.target.value)} disabled={!customerId} />
                  </label>
                )}
                {creditAmount > 0 && (
                  <label>
                    Ödəmə tarixi (nisyə)
                    <DateField value={dueOn} onChange={setDueOn} />
                  </label>
                )}
                {/* Qeyd bir toxunuşla açılır — satışı yavaşlatmır (KT-76) */}
                <NoteField key={saleId} value={note} onChange={setNote} />
                {allow('cash') ? (
                  <label>
                    Verilən nağd (boş — dəqiq qalıq)
                    <input type="number" min="0" step="0.01" value={cashGiven} onChange={(e) => setCashGiven(e.target.value)} />
                  </label>
                ) : (
                  cashDue > 0 && (
                    <p className="bad" role="alert">
                      Bu kassada nağd ödəniş bağlıdır — {formatMoney(cashDue)} kart və ya digər üsulla ödənilməlidir.
                    </p>
                  )
                )}
                {needPin && (
                  <label>
                    Menecer PİN-i (limit təsdiqi)
                    <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="off" />
                  </label>
                )}
                {/* Böyük cəm (KT-95): kassir və müştəri uzaqdan görsün */}
                <div className="sale-total" aria-live="polite">
                  <span className="sale-total-label">CƏMİ</span>
                  <strong className="sale-total-value">{formatMoney(total)}</strong>
                </div>
                <div className="sale-cash">
                  <span>
                    Nağd ödənəcək <strong>{formatMoney(cashDue)}</strong>
                  </span>
                  <span className="sale-change">
                    Qalıq pul <strong>{formatMoney(change)}</strong>
                  </span>
                </div>
                {preview?.warnings.map((w) => (
                  <p key={w} className="muted small" role="status">
                    {w}
                  </p>
                ))}
                <button type="submit" className="btn-primary btn-block" disabled={busy || cart.length === 0 || overpaid || cardPending || (!shift && (cashDue > 0 || cardAmount > 0)) || (!allow('cash') && cashDue > 0)}>
                  Sat
                </button>
              </form>
            </section>

            {canPay && !showDebt && (
              <button type="button" className="btn-small" onClick={() => setShowDebt(true)}>
                Borc ödənişi qəbul et
              </button>
            )}
            {canPay && showDebt && (
              <section className="card" aria-label="Borc ödənişi">
                <div className="card-head">
                  <h2>Borc ödənişi</h2>
                  <button type="button" className="link-btn" onClick={() => setShowDebt(false)}>
                    Bağla
                  </button>
                </div>
                <label className="field">
                  Müştəri
                  <select value={debtCustomerId} onChange={(e) => setDebtCustomerId(e.target.value)}>
                    <option value="">Seçin</option>
                    {customers
                      .filter((c) => c.balance !== 0)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · borc {formatMoney(c.balance)}
                        </option>
                      ))}
                  </select>
                </label>
                {debtCustomerId && customers.find((c) => c.id === debtCustomerId) && (
                  <DebtPayment
                    key={debtCustomerId}
                    customerId={debtCustomerId}
                    balance={customers.find((c) => c.id === debtCustomerId)!.balance}
                    registerId={registerId}
                    onPaid={() => {
                      loadCustomers();
                    }}
                  />
                )}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/** Şirkət satışından sonra Əlavə №N (KT-82): alıcının rejiminə görə — avtomatik, soruş və ya heç nə. Səhv satışı dayandırmır. */
function SaleAnnex({ docId }: { docId: string }) {
  const [state, setState] = useState<AnnexForSaleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .annexForSale(docId)
      .then(setState)
      .catch((e: unknown) => {
        // Paketdə müqavilə modulu yoxdursa (403) susur — satış artıq yazılıb
        if (!(e instanceof ApiError && e.status === 403)) setError(e instanceof Error ? e.message : 'Əlavə hazırlanmadı');
      });
  }, [docId]);
  const confirm = () => {
    setBusy(true);
    api
      .annexForSale(docId, true)
      .then(setState)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Əlavə hazırlanmadı'))
      .finally(() => setBusy(false));
  };
  if (error)
    return (
      <p className="bad" role="alert">
        Əlavə: {error}
      </p>
    );
  if (!state) return null;
  if (state.result === 'ask')
    return (
      <p>
        Müqavilə №{state.contractNumber} üzrə{' '}
        <button type="button" className="btn-small" disabled={busy} onClick={confirm}>
          Əlavə №{state.nextNo} hazırla
        </button>
      </p>
    );
  if (state.result !== 'created') return null;
  return (
    <p className="ok" role="status">
      Əlavə №{state.annex.annexNo} hazırlandı ·{' '}
      <a href={annexFileUrl(state.annex.id)} download>
        Word
      </a>
    </p>
  );
}
